import { useCallback, useEffect, useState } from 'react';
import { Settings, RefreshCw, Plug, CheckCircle2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import StatusPill from '../components/StatusPill';
import OpenAppLink from '../components/OpenAppLink';
import { secondaryButtonClass } from '../components/FilterBar';
import { formatDateTime } from '../lib/format';
import {
  fetchIntegrationStatus,
  retryIntegration,
  integrationLabel,
  normalizeIntegrationStatusValue,
  INTEGRATION_KEYS,
  getStatusLabel,
  type RawIntegrationStatus,
} from '../lib/api';

function Admin() {
  const [statuses, setStatuses] = useState<Record<string, RawIntegrationStatus | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.all(INTEGRATION_KEYS.map((name) => fetchIntegrationStatus(name)));
    const next: Record<string, RawIntegrationStatus | undefined> = {};
    INTEGRATION_KEYS.forEach((name, i) => {
      next[name] = results[i].ok ? results[i].data : undefined;
    });
    setStatuses(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async (name: string) => {
    setRetrying((prev) => new Set(prev).add(name));
    const result = await retryIntegration(name);
    if (result.ok && result.data) {
      toast.success(`${integrationLabel(name)}: ${getStatusLabel(normalizeIntegrationStatusValue(result.data.status))}`);
      setStatuses((prev) => ({ ...prev, [name]: result.data }));
    }
    setRetrying((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const configuredCount = Object.values(statuses).filter((s) => s?.configured).length;
  const connectedCount = Object.values(statuses).filter((s) => s?.connected).length;

  return (
    <PageShell
      title="Admin"
      subtitle="Integration health, connection status, and retry controls."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Integrations" value={String(INTEGRATION_KEYS.length)} tone="info" icon={<Plug size={18} />} />
        <StatCard label="Configured" value={loading ? '…' : String(configuredCount)} tone="neutral" />
        <StatCard label="Connected" value={loading ? '…' : String(connectedCount)} tone="success" />
        <StatCard
          label="Issues"
          value={loading ? '…' : String(INTEGRATION_KEYS.length - connectedCount)}
          tone={INTEGRATION_KEYS.length - connectedCount > 0 ? 'warning' : 'success'}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-zt-muted">Integration connections</div>
        <button type="button" onClick={load} className={secondaryButtonClass}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh all
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {INTEGRATION_KEYS.map((name) => {
          const status = statuses[name];
          const normalized = status ? normalizeIntegrationStatusValue(status.status) : 'unknown';
          return (
            <div key={name} className="rounded-xl border border-zt-border bg-zt-surface p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-white">{integrationLabel(name)}</h3>
                <StatusPill
                  status={normalized === 'ok' ? 'ok' : normalized === 'error' ? 'error' : normalized === 'warn' ? 'warn' : 'unknown'}
                  label={getStatusLabel(normalized)}
                />
              </div>
              {loading ? (
                <div className="h-16 animate-pulse rounded bg-zt-border" />
              ) : (
                <>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-zt-muted">Configured</span>
                      {status?.configured ? (
                        <CheckCircle2 size={16} className="text-zt-primary" />
                      ) : (
                        <XCircle size={16} className="text-zt-muted" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zt-muted">Connected</span>
                      {status?.connected ? (
                        <CheckCircle2 size={16} className="text-zt-primary" />
                      ) : (
                        <XCircle size={16} className="text-zt-danger" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zt-muted">Checked</span>
                      <span className="text-slate-200">{status ? formatDateTime(status.last_checked) : '—'}</span>
                    </div>
                  </div>
                  {status?.last_error && (
                    <div className="mt-3 truncate rounded bg-zt-danger/10 px-2 py-1.5 font-mono text-xs text-zt-danger" title={status.last_error}>
                      {status.last_error}
                    </div>
                  )}
                  {status?.configured && (
                    <div className="mt-3">
                      <OpenAppLink integrationKey={name} />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRetry(name)}
                    disabled={retrying.has(name)}
                    className={`mt-4 w-full ${secondaryButtonClass}`}
                  >
                    <RefreshCw size={14} className={retrying.has(name) ? 'animate-spin' : ''} /> Retry connection
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
        <div className="mb-2 flex items-center gap-2">
          <Settings className="text-zt-muted" size={18} />
          <h3 className="font-semibold text-white">Settings</h3>
        </div>
        <p className="text-sm text-zt-muted">
          Integration credentials and infrastructure settings are managed via environment
          configuration on the server and are not editable from the dashboard.
        </p>
      </div>
    </PageShell>
  );
}

export default Admin;
